import type { IntegrationType } from "@v2/features/settings/shop/integrations/IntegrationForm";
import type { ReploIntegration } from "schemas/generated/integration";
import type { IntegrationStatus } from "./IntegrationStatusTag";

import * as React from "react";

import { LabeledControl } from "@replo/design-system/blocks/labeled-control/LabeledControl";
import PageHeader from "@replo/design-system/blocks/page-header/PageHeader";
import { Button } from "@replo/design-system/components/button/Button";
import { Empty } from "@replo/design-system/components/empty/Empty";
import { Input } from "@replo/design-system/components/input/Input";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { Skeleton } from "@replo/design-system/components/skeleton/Skeleton";
import { Tag } from "@replo/design-system/components/tag/Tag";
import { toast } from "@replo/design-system/components/toast/Toast";
import { skipToken } from "@tanstack/react-query";
import { INTEGRATION_CONFIGS } from "@v2/features/settings/shop/integrations/config";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { integrationRouter } from "@v2/trpc/routes/integration";
import { Plug, Search } from "lucide-react";

import { AgentIntegrationsSection } from "./AgentIntegrationsSection";
import EnabledIntegrationsRow from "./EnabledIntegrationsRow";
import {
  ALL_CATEGORIZED_INTEGRATIONS,
  getCategoryForIntegration,
  INTEGRATION_CATEGORIES,
} from "./integrationCategories";
import IntegrationGridCard from "./IntegrationGridCard";
import RecommendedCarousel from "./RecommendedCarousel";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getStatusForIntegration({
  integration,
}: {
  integration: ReploIntegration | null;
}): IntegrationStatus {
  if (!integration) {
    return "not_connected";
  }
  if (integration.deletedAt) {
    return "not_connected";
  }
  // NOTE (Jasmine, 2026-02-17): Placeholder for expired detection — will be
  // driven by backend data (e.g. token expiry, failed health check) in the
  // future. When ready, check integration metadata here and return "expired".
  return "connected";
}

function matchesSearch({
  integrationType,
  query,
}: {
  integrationType: IntegrationType;
  query: string;
}): boolean {
  const config = INTEGRATION_CONFIGS[integrationType];
  const lowerQuery = query.toLowerCase();

  // Match on the integration's own title and description
  if (
    config.title.toLowerCase().includes(lowerQuery) ||
    config.description.toLowerCase().includes(lowerQuery)
  ) {
    return true;
  }

  // Match on the integration's category label, description, and keywords
  const category = getCategoryForIntegration({ type: integrationType });
  if (
    category &&
    (category.label.toLowerCase().includes(lowerQuery) ||
      category.description.toLowerCase().includes(lowerQuery) ||
      category.keywords.some((keyword) => keyword.includes(lowerQuery)))
  ) {
    return true;
  }

  return false;
}

// ---------------------------------------------------------------------------
// Skeleton loader
// ---------------------------------------------------------------------------

const IntegrationsSkeleton: React.FC = () => (
  <>
    <PageHeader title={<Skeleton className="h-8 w-48" />} />
    <Skeleton className="h-9 w-full rounded-lg mt-4" />
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pt-4">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-[140px] w-full rounded-xl" />
      ))}
    </div>
  </>
);

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export const IntegrationsPage: React.FC = () => {
  const projectId = useCurrentProjectId();

  const { data: integrations, isLoading } = integrationRouter.find.useQuery(
    projectId ? { projectId } : skipToken,
  );

  const [searchQuery, setSearchQuery] = React.useState("");

  // Build a lookup map: integration type → installed integration record
  const integrationsByType = React.useMemo(() => {
    const map = new Map<IntegrationType, ReploIntegration>();
    for (const integration of integrations ?? []) {
      if (
        integration.type !== "ShopifyIntegrationConfig" &&
        integration.type !== "StripeConnect"
      ) {
        map.set(integration.type, integration);
      }
    }
    return map;
  }, [integrations]);

  // Derive connected integrations (for the "Connected" section at top)
  const connectedTypes = React.useMemo(() => {
    return ALL_CATEGORIZED_INTEGRATIONS.filter((type) => {
      return integrationsByType.has(type);
    });
  }, [integrationsByType]);

  // Filter by search
  const filteredTypes = React.useMemo(() => {
    if (!searchQuery.trim()) {
      return null; // null = show grouped view
    }
    return ALL_CATEGORIZED_INTEGRATIONS.filter((type) => {
      return matchesSearch({ integrationType: type, query: searchQuery });
    });
  }, [searchQuery]);

  if (isLoading) {
    return (
      <div className="relative flex flex-col w-full h-full items-center">
        <IntegrationsSkeleton />
      </div>
    );
  }

  const renderCard = (type: IntegrationType) => {
    const existing = integrationsByType.get(type) ?? null;
    const status = getStatusForIntegration({ integration: existing });

    return (
      <IntegrationGridCard key={type} integrationType={type} status={status} />
    );
  };

  return (
    <div className="relative flex flex-col w-full h-full max-w-[800px] mx-auto">
      <PageHeader
        title="Integrations"
        description="Connect tools to power your commerce workflow."
        showDivider={false}
      />

      <Input
        placeholder="Search integrations…"
        size="base"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        startEnhancer={<Search size={14} className="text-muted" />}
        className="w-full"
        UNSAFE_className="mt-4"
      />

      <div className="flex flex-col gap-12 pt-6 pb-6">
        {/* Search results view */}
        {filteredTypes !== null ? (
          filteredTypes.length > 0 ? (
            <div className="flex flex-col gap-12">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredTypes.map(renderCard)}
              </div>
              <AgentIntegrationsSection searchQuery={searchQuery} />
            </div>
          ) : (
            <div className="flex flex-col gap-12">
              <Empty
                title={`No integrations match "${searchQuery}"`}
                description="Try a different search term."
                size="sm"
              />
              <AgentIntegrationsSection searchQuery={searchQuery} />
            </div>
          )
        ) : (
          <>
            {/* Connected section — compact pill carousel */}
            <EnabledIntegrationsRow connectedTypes={connectedTypes} />

            {/* Recommended carousel */}
            <RecommendedCarousel />

            {/* Category sections */}
            {INTEGRATION_CATEGORIES.map((category) => (
              <IntegrationSection
                key={category.id}
                title={category.label}
                subtitle={category.description}
              >
                {category.integrations.map(renderCard)}
              </IntegrationSection>
            ))}

            {/* Productivity integrations */}
            <AgentIntegrationsSection searchQuery="" />

            {/* Request integrations card */}
            <RequestIntegrationsCard />
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Section wrapper
// ---------------------------------------------------------------------------

const IntegrationSection: React.FC<
  React.PropsWithChildren<{
    title: string;
    subtitle?: string;
    count?: number;
  }>
> = ({ title, subtitle, count, children }) => (
  <div className="flex flex-col gap-3">
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-default">{title}</span>
      {count !== undefined && (
        <Tag variant="neutral" size="sm">
          {count}
        </Tag>
      )}
    </div>
    {subtitle && <span className="text-sm text-muted -mt-2">{subtitle}</span>}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {children}
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Request integrations card
// ---------------------------------------------------------------------------

const RequestIntegrationsCard: React.FC = () => {
  const projectId = useCurrentProjectId();
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [integrationName, setIntegrationName] = React.useState("");

  const {
    mutate: recordNewIntegrationRequest,
    isPending: isRequestingIntegration,
  } = integrationRouter.recordNewIntegrationRequest.useMutation({
    onSuccess: () => {
      toast.success({
        title: "Request submitted",
        description: "Thanks! We have received your integration request.",
      });
      setIsModalOpen(false);
      setIntegrationName("");
    },
    onError: () => {
      toast.error({
        title: "Failed to submit request",
        description: "Please try again or contact support@replo.app.",
      });
    },
  });

  const handleRequest = () => {
    setIsModalOpen(true);
  };

  const handleSubmit = () => {
    const trimmedIntegrationName = integrationName.trim();
    if (!projectId || !trimmedIntegrationName) {
      return;
    }

    recordNewIntegrationRequest({
      projectId,
      integrationName: trimmedIntegrationName,
    });
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setIntegrationName("");
  };

  return (
    <>
      <div className="relative flex flex-col gap-4 rounded-xl border-0.5 border-border bg-subtle p-6 overflow-hidden shadow-xs">
        {/* Soft gradient overlay at top with blur */}
        <div
          className="absolute top-0 left-0 right-0 h-12 opacity-20 blur-xl"
          style={{
            background: `linear-gradient(to right, #B7FEAE 0%, #FFD873 33%, #FE816E 66%, #DA6AF3 100%)`,
          }}
        />

        {/* Content */}
        <div className="flex items-center justify-between gap-4 relative z-10">
          <div className="flex flex-col gap-3 flex-1">
            <div className="shrink-0">
              <Plug size={20} className="text-default" strokeWidth={1.5} />
            </div>
            <div className="flex flex-col gap-1.5">
              <span className="header-h4 text-default">
                Missing an integration?
              </span>
              <span className="text-sm text-muted">
                Request new integrations or support the ones you care about
              </span>
            </div>
          </div>
          <Button
            variant="secondary"
            size="base"
            onClick={handleRequest}
            className="shrink-0"
          >
            Request
          </Button>
        </div>
      </div>

      {/* Request Integration Modal */}
      <Modal
        open={isModalOpen}
        onOpenChange={handleClose}
        size="base"
        title="Request an Integration"
        description="Let us know which integration you'd like to see added to Replo."
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="tertiary"
              size="base"
              disabled={isRequestingIntegration}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="base"
              loading={isRequestingIntegration}
              onClick={handleSubmit}
              disabled={!integrationName.trim()}
            >
              Submit Request
            </Button>
          </div>
        }
      >
        <LabeledControl label="Integration Name" size="sm">
          <Input
            placeholder="e.g., Stripe, Mailchimp, HubSpot"
            size="base"
            value={integrationName}
            disabled={isRequestingIntegration}
            onChange={(e) => setIntegrationName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && integrationName.trim()) {
                handleSubmit();
              }
            }}
          />
        </LabeledControl>
      </Modal>
    </>
  );
};
